import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { useJsData } from '@bingads-webui-cc-react/js-data-rest';

import { LibraryEmptyView } from '../empty-view/library-page-empty-view';
import { LibraryFilterBar, FilterContext } from '../../filter-bar';

import { SectionTilesGrid, DEFAULT_CONFIG as DEFAULT_GRID_CONFIG } from '../ads-studio-tile-grid';

import {
  GridSectionName,
  DEFAULT_PAGE_SIZE,
  AdsStudioPageName,
  FolderAssetType,
  CSMLTimeFrame,
  getDateAddedChoiceOptions,
  getUpdatedAssets,
  AIGCHandler,
  isValidXandrAccount,
} from '../../model';

import { FolderPathBreadcrumb } from '../folder-path-breadcrumb';
import { AdsStudioSpinner } from '../ads-studio-spinner';

import { useAdsStudioLibraryPageContentStyle } from './styles/library-page-style';
import {
  isSectionDataLoadingStatus,
  isSectionDataEmptyStatus,
} from './sections-utils';
import { HiddenDivForAnnounce } from '../hidden-div-for-announce';

const overlaySpinnerStyles = { margin: 200 };
// const bottomSpinnerStyles = { marginTop: 20, marginBottom: 100 };

const FOLDER_GRID_CONFIG = {
  ...DEFAULT_GRID_CONFIG,
  aspectRatio: undefined,
  rowHeight: 110,
  // width: 320,
};

const AdsStudioLibraryPageContentInternal = ({
  dataService,
  renderHeader,
  renderGridCell,
  onFailedToLoadData,
  dateFormat,
  ianaTimeZone,
  // assetLibraryUserPreferences,
  // assetLibraryAccountPreferences,
  adsStudioUserPreferences,
  reload,
  logger,
  i18n,
  aid,
  cid,
  oDataConfig,
  dataLoaded,
  perfMarker,
  displayFolderPath,
  reloadingFolders,
  assetsToLoadForSubfolders,
  renderToolbar,
  renderEmptyView,

  // multi-selection props
  multiSelectionEnabled,
  onSelectionChange,
  // onFolderNavigationChange,
  sectionSelectedItems,
  //
  // searchLocationFilterState,
  // setSearchLocationFilterState,

  // realUsedFlagSuffix,
  isAssetLibraryAIGCMVPEnabled,
  isAdsStudioAIGCMVPEnabled,
  isAIGCRegionRestricted,
  notificationApi,
  // isClickableOutsideFocusTrap,

  preDefinedAssetSources,
  // disabledAssetSources,
  onCreateFolderClick,
  navigation,

  gridScrollContainer,
  permissions,
}) => {
  const isXandrIntEnabled = useMemo(() => permissions.dynamic.XandrIntegrationEnabled, [permissions]);
  // eslint-disable-next-line no-restricted-globals
  const { data: account } = useJsData(jsData => (isXandrIntEnabled ? jsData.Account.find(aid) : Promise.resolve()), [aid, isXandrIntEnabled]);
  const isXandrIntegrationEnabled = useMemo(() => (isXandrIntEnabled ? isValidXandrAccount(account) : false), [account, isXandrIntEnabled]);

  const classes = useAdsStudioLibraryPageContentStyle();
  const [announcedText, setAnnouncedText] = useState('');

  const { filterBar } = useContext(FilterContext);
  const {
    isImageSelected,
    isVideoSelected,
    searchText: searchBoxText,
    sortBy,
    dateAddedFilter,
    dateAddedFilterCustomDateRange,
    importSource,
    assetSources,
    minWidthFilter,
    minHeightFilter,
    minDurationFilter,
    maxDurationFilter,
  } = useMemo(() => filterBar.getFilters(), [filterBar]);

  const isAnyFilterSet = searchBoxText || importSource || isImageSelected || isVideoSelected
      || minWidthFilter || minHeightFilter || minDurationFilter || maxDurationFilter
      || (dateAddedFilter && dateAddedFilter !== CSMLTimeFrame.Anytime) || !_.isEmpty(assetSources);

  const {
    assets,
    totalAssetCount,
    totalSubfoldersCount,
    imageCroppingTasks,
    loadData,
    loadMoreData,
    folderPath,
    navigateToFolderInPath,
    folders,
    currentFolderId,
    videoUpdated,
    dataOwnerPageName,
  } = dataService || {};

  const openSystemUploadRef = useRef(null);

  const onUploadLocalFileClick = useCallback(() => {
    openSystemUploadRef.current = {};
    navigation.gotoUploadFilesPanelPage({
      aid, cid, oDataConfig, i18n, openSystemUpload: openSystemUploadRef.current,
    });
  }, [aid, cid, i18n, navigation, oDataConfig]);

  const onStockButtonClick = useCallback((props) => {
    if (logger && logger.logTrace) {
      logger.logTrace('Add asset from shutterstock');
    }
    navigation.gotoStockImagePanelPage(props);
  }, [logger, navigation]);

  const onInvestButtonClick = useCallback((props) => {
    if (logger && logger.logTrace) {
      logger.logTrace('Add asset from invest');
    }
    navigation.gotoInvestImagePanelPage(props);
  }, [logger, navigation]);

  const onWebsiteButtonClick = useCallback((props) => {
    if (logger && logger.logTrace) {
      logger.logTrace('Add asset from website');
    }
    navigation.gotoWebsiteImagePanelPage(props);
  }, [logger, navigation]);

  const onUploadLocalFolderClick = useCallback(() => {
    openSystemUploadRef.current = {};
    navigation.gotoUploadFolderPanelPage({
      aid, cid, oDataConfig, i18n, openSystemUpload: openSystemUploadRef.current,
    });
  }, [aid, cid, i18n, navigation, oDataConfig]);

  const onUploadVideoClick = useCallback(() => {
    if (logger && logger.logTrace) {
      logger.logTrace('Add video from URl');
    }

    if (isXandrIntegrationEnabled) {
      navigation.gotoMixedVideoPanelPage({
        aid, cid, oDataConfig, i18n,
      });
    } else {
      navigation.gotoAddVideoPanelPage({
        aid, cid, oDataConfig, i18n,
      });
    }
  }, [aid, cid, i18n, logger, navigation, oDataConfig, isXandrIntegrationEnabled]);

  const onUploadImageClick = useCallback(() => {
    navigation.gotoMixedImagePanelPage({
      aid, cid, oDataConfig, i18n,
    });
  }, [aid, cid, i18n, navigation, oDataConfig]);

  // // load assets for initial page load, or after any filters are changed
  const loadedAssetList = useMemo(() => getUpdatedAssets({
    assets,
    updates: {
      imageCroppingTasks,
      videoUpdated,
    },
  }), [assets, imageCroppingTasks, videoUpdated]);

  const onRenderCell = useCallback(({ item, sectionName, ...others }) => {
    if (renderGridCell) {
      const cell = renderGridCell({
        item, searchText: searchBoxText, sectionName, ...others,
      });
      if (cell) return cell;
    }
    return null;
  }, [renderGridCell, searchBoxText]);

  const [isPageInited, setPageInited] = useState(false);
  // const [isGridInited, setGridInited] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const loadDataAsync = useCallback(() => {
    // get selected date range for date added filter
    let dateAddedDateRange;
    if (dateAddedFilter === CSMLTimeFrame.Custom) {
      dateAddedDateRange = dateAddedFilterCustomDateRange;
    } else {
      const dateAddedChoiceOptions = getDateAddedChoiceOptions(i18n, ianaTimeZone);
      const { range } = _.find(dateAddedChoiceOptions, o => o.key === dateAddedFilter) || {};
      dateAddedDateRange = range;
    }

    const usePreDefinedAssetSources = preDefinedAssetSources.length > 0 && assetSources.length === 0;
    let assetTypeFilter = null;
    if (isImageSelected || isVideoSelected) {
      assetTypeFilter = isImageSelected ? FolderAssetType.Image : FolderAssetType.Video;
    }
    let libraryPageLoadDataPerfMarker;
    if (perfMarker) {
      libraryPageLoadDataPerfMarker = perfMarker.createChild('libraryPageLoadData');
    }
    return loadData({
      searchText: searchBoxText,
      minImageWidth: minWidthFilter,
      minImageHeight: minHeightFilter,
      minVideoDuration: minDurationFilter,
      maxVideoDuration: maxDurationFilter,
      dateAddedDateRange,
      sortBy,
      importSource,
      assetSources: usePreDefinedAssetSources ? preDefinedAssetSources : assetSources,
      assetsToLoadForSubfolders,
      assetTypeFilter,
      isGlobalSearch: false,
      includeParentHierarchy: false, // include parent hierarchy when global searching
      pageName: AdsStudioPageName.LibraryPage,
      perfMarker: libraryPageLoadDataPerfMarker,
    });
  }, [assetSources, assetsToLoadForSubfolders, dateAddedFilter, dateAddedFilterCustomDateRange, i18n, ianaTimeZone, importSource, isImageSelected, isVideoSelected, loadData, maxDurationFilter, minDurationFilter, minHeightFilter, minWidthFilter, preDefinedAssetSources, searchBoxText, sortBy]);

  const bulkTookbar = useMemo(() => (renderToolbar ? renderToolbar() : null), [renderToolbar]);

  const onFolderPathClicked = useCallback((item) => {
    logger.logTrace('Navigate to folder in path', { folderId: item.key });
    navigateToFolderInPath({ id: item.key });
  }, [navigateToFolderInPath, logger]);

  const onRenderHeader = useCallback(() => (
    !renderHeader ? (
      <div className={classnames('library-page-header', bulkTookbar && 'show-bulk-toolbar')}>
        <div className={classnames('library-page-header-top-wrapper')}>
          {/* display folder path */}
          {displayFolderPath &&
            <FolderPathBreadcrumb
              folderPath={folderPath}
              onItemClick={onFolderPathClicked}
            />
          }
          <LibraryFilterBar
            i18n={i18n}
            logger={logger}
            ianaTimeZone={ianaTimeZone}
            dateFormat={dateFormat}
            isAdsStudioAIGCMVPEnabled={isAdsStudioAIGCMVPEnabled && !isAIGCRegionRestricted}
            onUploadLocalFileClick={onUploadLocalFileClick}
            onUploadLocalFolderClick={onUploadLocalFolderClick}
            onUploadImageClick={onUploadImageClick}
            onUploadVideoClick={onUploadVideoClick}
            onAIGenerateClick={() => {
              AIGCHandler(isAdsStudioAIGCMVPEnabled, isAIGCRegionRestricted, notificationApi, i18n, () => {
                navigation.gotoAIGenerationPanelPage({ folderId: currentFolderId });
              });
            }}
            onCreateFolderClick={onCreateFolderClick}
            userPreference={adsStudioUserPreferences}
          />
        </div>
        <div className="library-page-header-toolbar">
          {bulkTookbar}
        </div>
      </div>

    ) : (
      renderHeader()
    )
  ), [adsStudioUserPreferences, bulkTookbar, currentFolderId, dateFormat, displayFolderPath, folderPath, i18n, ianaTimeZone, isAIGCRegionRestricted, isAdsStudioAIGCMVPEnabled, logger, navigation, notificationApi, onCreateFolderClick, onFolderPathClicked, onUploadImageClick, onUploadLocalFileClick, onUploadLocalFolderClick, onUploadVideoClick, renderHeader]);

  const foldersGridSection = useMemo(() => ({
    sectionName: GridSectionName.Folder,
    sectionTitle: i18n.getString(_TL_('Folders')),
    sectionClassName: 'ads-studio-folders-section',
    itemKey: 'folderId',
    items: folders,
    totalItemCount: totalSubfoldersCount,
    config: {
      ...FOLDER_GRID_CONFIG,
      scrollContainer: gridScrollContainer,
    },
    selectedItems: sectionSelectedItems && sectionSelectedItems[GridSectionName.Folder],
  }), [folders, gridScrollContainer, i18n, sectionSelectedItems, totalSubfoldersCount]);

  const assetsGridSection = useMemo(() => ({
    sectionName: GridSectionName.Asset,
    sectionTitle: i18n.getString(_TL_('Assets')),
    sectionClassName: 'ads-studio-assets-section',
    itemKey: 'id',
    items: loadedAssetList,
    totalItemCount: totalAssetCount,
    selectedItems: sectionSelectedItems && sectionSelectedItems[GridSectionName.Asset],
    config: {
      scrollContainer: gridScrollContainer,
      minItemHeight: 42,
    },
  }), [gridScrollContainer, i18n, loadedAssetList, sectionSelectedItems, totalAssetCount]);

  const sectionGridData = useMemo(
    () => ([foldersGridSection, assetsGridSection]),
    [assetsGridSection, foldersGridSection]
  );

  // init page
  useEffect(() => {
    if (!isPageInited) {
      setPageInited(true);
    }
  }, [isPageInited]);

  useEffect(() => {
    if (isAssetLibraryAIGCMVPEnabled) {
      setClarityTag({ AssetLibraryCopilot: 'true' });
    }
  }, [isAssetLibraryAIGCMVPEnabled]);

  // load data for initial page load
  useEffect(() => {
    if (!isPageInited) {
      // wait until page is initialized
      return;
    }

    if (reload) { // Important! Add a trivial log so that reload will be an explicit dep.
      logger.logTrace('force to reload');
    }
    if (perfMarker) {
      perfMarker.willFetchData();
    }
    setLoadingData(true);
    loadDataAsync()
      .tapCatch((err) => { // throw out so that this error will be shown in console for easy debugging
        onFailedToLoadData(err);
      }).finally(() => {
        setLoadingData(false);
        if (perfMarker) {
          perfMarker.willRender();
        }
        dataLoaded();
        // setGridInited(true);
      });
  }, [isPageInited, loadDataAsync, onFailedToLoadData, reload, logger]);

  const loadNextPage = useCallback(() => {
    loadMoreData({ count: DEFAULT_PAGE_SIZE }).catch((err) => {
      onFailedToLoadData(err);
    });
  }, [loadMoreData, onFailedToLoadData]);

  const isPageLoading = !isPageInited
    || loadingData || isSectionDataLoadingStatus(sectionGridData) || reloadingFolders;

  const hadNoData = isSectionDataEmptyStatus(sectionGridData);

  useEffect(() => {
    if (!isPageLoading && hadNoData && !_.isFunction(renderEmptyView) && isAnyFilterSet) {
      setAnnouncedText(i18n.getString(_TL_('No assets match your search. Please try again.')));
    } else {
      setAnnouncedText('');
    }
  }, [hadNoData, i18n, isAnyFilterSet, isPageLoading, renderEmptyView]);

  return (
    <div className={classnames('ads-studio-page', classes.container, classes.injectedContentContainer)}>
      {/*  grid header or toolbar */}
      {onRenderHeader()}

      {/* grid content */}
      <div className="ads-studio-grid-container">
        {(!dataOwnerPageName || dataOwnerPageName === AdsStudioPageName.LibraryPage) && (
        <SectionTilesGrid
          sectionData={sectionGridData}
          onRenderCell={onRenderCell}
          loadNextPage={loadNextPage}
          multiSelectionEnabled={multiSelectionEnabled}
          onSelectionChange={onSelectionChange}
        />
          )}
      </div>
      {/* show loading indicator */}
      {isPageLoading && (
        <Overlay>
          <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={overlaySpinnerStyles} />
        </Overlay>
      )}

      {/* no content */}
      {(!isPageLoading && hadNoData) && (
      <div className={classnames('ads-studio-no-data', classes.emptyViewContainer)}>
        {
          renderEmptyView ? renderEmptyView() : (
            <LibraryEmptyView
              i18n={i18n}
              isInSubfolder={!!(folderPath && folderPath.length > 1)}
              isAnyFilterSet={isAnyFilterSet}
              onClickCreate={() => {
                AIGCHandler(isAdsStudioAIGCMVPEnabled, isAIGCRegionRestricted, notificationApi, i18n, () => {
                  navigation.gotoAIGenerationPanelPage({ folderId: currentFolderId });
                });
              }}
              onClickUpload={onUploadLocalFileClick}
              onClickStock={onStockButtonClick}
              onClickWebsite={onWebsiteButtonClick}
              onClickInvest={onInvestButtonClick}
              isXandrIntegrationEnabled={isXandrIntegrationEnabled}
            />
          )
        }
      </div>
        )}
      <HiddenDivForAnnounce toAnnounce={announcedText} />
    </div>
  );
};

AdsStudioLibraryPageContentInternal.propTypes = {
  dataService: PropTypes.shape({
    assets: PropTypes.arrayOf(PropTypes.shape({})),
    totalAssetCount: PropTypes.number,
    imageCroppingTasks: PropTypes.shape({}),
    loadData: PropTypes.func,
    loadMoreData: PropTypes.func,
  }).isRequired,
  onFailedToLoadData: PropTypes.func,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  ianaTimeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    getToday: PropTypes.func,
    formatCivilDate: PropTypes.func,
  }).isRequired,
  assetLibraryUserPreferences: PropTypes.shape({
    sortBy: PropTypes.string,
    setSortBy: PropTypes.func,
  }),
  assetLibraryAccountPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}), // if changed, force to reload
  renderHeader: PropTypes.func,
  renderGridCell: PropTypes.func,
  renderToolbar: PropTypes.func,

  dataLoaded: PropTypes.func,
  displayFolderPath: PropTypes.bool,
  reloadingFolders: PropTypes.bool,
  assetsToLoadForSubfolders: PropTypes.number,

  // multi selection
  multiSelectionEnabled: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  // onFolderNavigationChange: PropTypes.func,
  sectionSelectedItems: PropTypes.shape({}),

  // searchLocationFilterState: PropTypes.string,
  // setSearchLocationFilterState: PropTypes.func,

  // realUsedFlagSuffix: PropTypes.string,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool,
  isAdsStudioAIGCMVPEnabled: PropTypes.bool,
  // isClickableOutsideFocusTrap: PropTypes.bool,

  // custom filter
  preDefinedAssetSources: PropTypes.arrayOf(PropTypes.shape({})),
  // disabledAssetSources: PropTypes.arrayOf(PropTypes.string),
  onCreateFolderClick: PropTypes.func,
  navigation: PropTypes.shape({
    gotoUploadFilesPanelPage: PropTypes.func,
    gotoUploadFolderPanelPage: PropTypes.func,
    gotoWebsiteImagePanelPage: PropTypes.func,
    gotoStockImagePanelPage: PropTypes.func,
    gotoAddVideoPanelPage: PropTypes.func,
    gotoAIGenerationPanelPage: PropTypes.func,
    gotoMixedImagePanelPage: PropTypes.func,
    gotoMixedVideoPanelPage: PropTypes.func,
    gotoInvestImagePanelPage: PropTypes.func,
  }),
  gridScrollContainer: PropTypes.string,
  isAIGCRegionRestricted: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),

};

AdsStudioLibraryPageContentInternal.defaultProps = {
  assetLibraryUserPreferences: null,
  assetLibraryAccountPreferences: null,
  reload: null,
  renderHeader: null,
  renderGridCell: null,
  renderToolbar: null,
  onFailedToLoadData: () => null,
  dataLoaded: () => null,
  displayFolderPath: false,
  reloadingFolders: false,

  multiSelectionEnabled: false,
  onSelectionChange: undefined,
  // onFolderNavigationChange: _.noop,
  sectionSelectedItems: undefined,

  assetsToLoadForSubfolders: 1,

  // searchLocationFilterState: undefined,
  // setSearchLocationFilterState: undefined,

  // realUsedFlagSuffix: '',
  isAssetLibraryAIGCMVPEnabled: false,
  isAdsStudioAIGCMVPEnabled: false,
  // isClickableOutsideFocusTrap: false,

  preDefinedAssetSources: [],
  // disabledAssetSources: [],
  onCreateFolderClick: undefined,
  navigation: {},
  gridScrollContainer: '[role="main"], .app-content-container-col',
  isAIGCRegionRestricted: false,
  permissions: { dynamic: {} },
};

export const AdsStudioLibraryPageContent = AdsStudioLibraryPageContentInternal;
